import {
  useApiUrl,
  useCreate,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { notifyMutationError } from "@/lib/repository-utils";
import { Sentry } from "@/lib/sentry";
import { parseRead } from "@/lib/zod/read-schema";
import {
  ArtistViewType,
  artistViewSchema,
} from "@/pages/artists/schemas/artist";
import { ArtistAIEnrichmentType } from "@/pages/artists/schemas/artist.aienrichment.form";
import {
  ArtistSpotifyEnrichmentType,
  CreateArtistFormType,
} from "@/pages/artists/schemas/artist.create.form";
import { ArtistEditFormType } from "@/pages/artists/schemas/artist.edit.form";
import {
  ArtistNestedType,
  artistNestedSchema,
} from "@/pages/artists/schemas/artist.nested.form";
import { SpotifyArtist } from "@/pages/artists/types/spotify-artist";

export interface ArtistSummary {
  id: string;
  name: string;
  personalName?: string | null;
  genres?: Array<{ id: string; name: string }>;
  media?: { photos?: Array<{ url: string }> } | null;
  stats?: { spotify?: { followers?: number } } | null;
}

export const useArtistRepository = () => {
  const apiUrl = useApiUrl();
  const { custom, getOne, getList } = useDataProvider()();
  const { open } = useNotification();
  const { mutateAsync } = useCreate({ resource: "artists" });
  const { mutateAsync: updateAsync } = useUpdate({ resource: "artists" });
  const { edit } = useNavigation();

  async function get(id: string): Promise<ArtistViewType> {
    const response = await getOne({ id, resource: "artists" });
    // Lenient read + raw fallback: a hard `.parse()` here used to blank
    // the whole artist edit page on one unexpected field.
    return parseRead(artistViewSchema, response, "artist");
  }

  async function search(query: string): Promise<ArtistNestedType[]> {
    try {
      if (!query) return [];
      // Use the same Refine getList -> /artists/explore path the artist list page
      // and venue search use (proven). The previous raw `custom` GET diverged from
      // it (trailing-slash URL + hand-built params) and failed against the explore
      // endpoint. getList builds the Typesense params and api-version consistently.
      const sanitizedQuery = query.replace(/\s-\s/g, " ");
      const response = await getList({
        resource: "artists",
        pagination: { currentPage: 0, pageSize: 25 },
        filters: query.startsWith('"')
          ? [{ field: "name", operator: "eq", value: query }]
          : [],
        meta: {
          sortBy: "_text_match:desc",
          query: sanitizedQuery,
          queryBy:
            "enrichment.transliterations.el,enrichment.transliterations.en,name",
        },
      });
      return response.data.map((i: any) =>
        artistNestedSchema.parse({
          ...i?.document,
          photo: i?.document?.media?.photos?.[0],
        }),
      );
    } catch (ex) {
      console.error("Artist search failed due to:", ex);
      open?.({
        message: `Artist search failed due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function generateId(): Promise<string> {
    const result = await custom?.({
      url: ``,
      meta: {
        urlPath: "artists/id",
      },
      method: "get",
    });
    if (!result?.data) {
      open?.({
        message: "ID generation failed. Please try again later!",
        type: "error",
      });
      throw new Error("ID Generation failure");
    }

    return result?.data?.toString()!;
  }

  async function create(values: CreateArtistFormType): Promise<void> {
    Sentry.addBreadcrumb({
      category: "artists.create",
      message: "Creating artist",
      level: "info",
      data: { name: values.name },
    });
    await mutateAsync?.(
      { values },
      {
        onSuccess: (data) => {
          console.debug("Successful artist creation", data);
          const id = (data as any).id;
          Sentry.addBreadcrumb({
            category: "artists.create",
            message: "Artist created successfully",
            level: "info",
            data: { id, name: values.name },
          });
          edit("artists", id, "replace");
          open?.({
            type: "success",
            message: "Successfully created artist",
            description: `You created an artist called ${values.name}`,
          });
        },
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: "artists.create",
            message: "Failed to create artist",
            level: "error",
            data: { name: values.name },
          });
          Sentry.captureException(error);
          console.error("Failed to create artist with values", values, error);
          notifyMutationError(open, "Create artist request", error);
        },
      },
    );
  }
  async function update(values: Partial<ArtistEditFormType>) {
    Sentry.addBreadcrumb({
      category: "artists.update",
      message: "Updating artist",
      level: "info",
      data: { id: values.id, name: values.name },
    });
    const response = await updateAsync?.(
      { id: values.id!, values },
      {
        onSuccess: (data) => {
          console.debug("Successful artist update", data);
          Sentry.addBreadcrumb({
            category: "artists.update",
            message: "Artist updated successfully",
            level: "info",
            data: { id: values.id },
          });
          edit("artists", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated artist",
            description: `You updated an artist called ${(data as any).name}`,
          });
        },
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: "artists.update",
            message: "Failed to update artist",
            level: "error",
            data: { id: values.id },
          });
          Sentry.captureException(error);
          console.error("Failed to update artist with values", values, error);
          notifyMutationError(open, "Update artist request", error);
        },
      },
    );

    return response.data.id?.toString()!;
  }

  async function searchSpotify(value: string): Promise<SpotifyArtist[]> {
    try {
      const response = await custom?.({
        url: `${apiUrl}/spotify`,
        method: "post",
        payload: { query: value, type: 2, offset: 0, limit: 25 },
      });

      return response?.data?.artists?.items;
    } catch (ex) {
      console.error("Failed to search spotify due to:", ex);
      open?.({
        message: `Failed to search spotify due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function getSpotifyArtistFromLink(
    url: string,
  ): Promise<SpotifyArtist | undefined> {
    const extractSpotifyArtistId = (url: string): string | null => {
      const regex = /https:\/\/open\.spotify\.com\/artist\/([a-zA-Z0-9]+)/;
      const match = url.match(regex);
      return match ? match[1] : null;
    };

    const artistId = extractSpotifyArtistId(url);
    if (artistId) {
      try {
        // Perform the fetch operation using the extracted artistId
        const response = await custom?.({
          url: `${apiUrl}/spotify/artist/spotify`,
          method: "get",
          query: {
            id: artistId,
          },
        });
        console.debug("Response", response);
        return response?.data as SpotifyArtist;
      } catch (ex) {
        console.error("Failed to get artist from spotify due to:", ex);

        open?.({
          type: "error",
          message: `Failed to get artist from spotify`,
          description: toHumanErrorMessage(ex),
        });
      }
    } else {
      open?.({
        message: "Invalid Spotify URL",
        description: "Try again with a valid artist profile url",
        type: "error",
      });
    }
  }

  async function enrichSpotifyArtist(
    spotifyId: string,
    name: string,
    spotifyArtist: SpotifyArtist,
  ): Promise<ArtistSpotifyEnrichmentType | null> {
    try {
      const response = await custom?.({
        method: "post",
        url: `${apiUrl}/artists/enrich/spotify`,
        meta: {
          urlPath: "",
        },
        payload: {
          name: name,
          spotifyId: spotifyId,
          popularity: spotifyArtist.popularity,
          followers: spotifyArtist.followers.total,
          imageUrl: spotifyArtist.images?.[0]?.url,
          genres: spotifyArtist.genres,
        },
      });

      return response?.data as ArtistSpotifyEnrichmentType;
    } catch (ex) {
      console.error("Error enriching artist with Spotify data", ex);
      open?.({
        type: "error",
        message: `Spotify Enrichment Failed due to ${toHumanErrorMessage(ex)}`,
        description:
          "There was an error enriching the artist with Spotify data. Please check the console for more details.",
      });
      throw ex;
    }
  }

  async function linkSpotify(
    artistId: string,
    spotifyArtist: SpotifyArtist,
  ): Promise<boolean> {
    Sentry.addBreadcrumb({
      category: "artists.linkSpotify",
      message: "Linking artist to Spotify",
      level: "info",
      data: { artistId, spotifyId: spotifyArtist.id },
    });
    try {
      await custom?.({
        url: `${apiUrl}/artists/link-spotify`,
        method: "post",
        payload: {
          artistId,
          spotifyId: spotifyArtist.id,
          name: spotifyArtist.name,
          popularity: spotifyArtist.popularity,
          followers: spotifyArtist.followers.total,
          imageUrl: spotifyArtist.images?.[0]?.url,
          genres: spotifyArtist.genres,
        },
      });
      open?.({
        type: "success",
        message: "Spotify artist linked",
        description: `Linked ${spotifyArtist.name} and refreshed Spotify data.`,
      });
      return true;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to link Spotify artist", artistId, error);
      notifyMutationError(open, "Link Spotify artist", error);
      return false;
    }
  }

  async function triggerEnrich(
    id: string,
    params: { instagramScreenshot?: string },
  ): Promise<boolean> {
    Sentry.addBreadcrumb({
      category: "artists.enrich",
      message: "Triggering artist enrichment",
      level: "info",
      data: { id },
    });
    try {
      await custom?.({
        url: `${apiUrl}/artists/enrich`,
        method: "post",
        payload: { id, ...params },
      });
      return true;
    } catch (error) {
      Sentry.addBreadcrumb({
        category: "artists.enrich",
        message: "Failed to trigger artist enrichment",
        level: "error",
        data: { id },
      });
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to trigger enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function updateEnrichment(
    id: string,
    enrichment: ArtistAIEnrichmentType,
  ): Promise<boolean> {
    try {
      await custom?.({
        url: `${apiUrl}/artists/enrich`,
        method: "put",
        payload: { id, enrichment },
      });
      open?.({
        type: "success",
        message: "Enrichment approved",
        description: "The enrichment data has been saved successfully.",
      });
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to save enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function nearestNeighbours(
    embedding: number[],
    limit: number,
  ): Promise<ArtistSummary[]> {
    const response = await custom?.({
      url: `${apiUrl}/search/nearest-neighbours-by-embedding`,
      method: "post",
      payload: { Embedding: embedding, TargetType: "artist", Limit: limit },
    });
    return (response?.data as ArtistSummary[]) ?? [];
  }

  /**
   * Move `sourceId`'s followers, events and notification subscriptions onto
   * `targetId`. The source artist keeps its document -- hidden, marked with
   * `mergedInto` and emptied of events -- so the move stays reversible and its
   * public URLs keep resolving. See `unmerge` for the way back.
   */
  async function merge(sourceId: string, targetId: string) {
    try {
      const response = await custom?.({
        url: `${apiUrl}/artists/merge`,
        method: "post",
        payload: {
          sourceArtistId: sourceId,
          targetArtistId: targetId,
        },
      });

      const { eventsRewritten, followersMoved, warnings } =
        response?.data ?? {};
      open?.({
        type: "success",
        message: "Successfully merged artists",
        // Warnings are non-fatal: a Knock subscription that would not move does
        // not roll the merge back, so the curator has to be told about it.
        description: [
          `${eventsRewritten ?? 0} events rewritten, ${followersMoved ?? 0} followers moved`,
          ...(warnings ?? []),
        ].join("\n"),
      });
    } catch (e: any) {
      console.error(e);
      open?.({
        type: "error",
        message: "Failed to merge artist",
        description: toHumanErrorMessage(e),
      });
      throw e;
    }
  }

  /**
   * Undo a merge: put the source artist back on the lineups it was taken off,
   * return the followers it brought, and clear `mergedInto`/`hide`.
   *
   * `eventsSkipped` is not an error. An event that changed its lineup after the
   * merge is left alone rather than overwritten, and the curator is told how many.
   */
  async function unmerge(sourceId: string) {
    try {
      const response = await custom?.({
        url: `${apiUrl}/artists/unmerge`,
        method: "post",
        payload: {
          sourceArtistId: sourceId,
        },
      });

      const { eventsRestored, eventsSkipped, followersRestored, warnings } =
        response?.data ?? {};
      open?.({
        type: "success",
        message: "Successfully unmerged artist",
        description: [
          `${eventsRestored ?? 0} events restored, ${followersRestored ?? 0} followers returned`,
          ...(eventsSkipped
            ? [`${eventsSkipped} events skipped (their lineup changed since the merge)`]
            : []),
          ...(warnings ?? []),
        ].join("\n"),
      });
    } catch (e: any) {
      console.error(e);
      open?.({
        type: "error",
        message: "Failed to unmerge artist",
        description: toHumanErrorMessage(e),
      });
      throw e;
    }
  }

  return {
    get,
    search,
    generateId,
    searchSpotify,
    getSpotifyArtistFromLink,
    enrichSpotifyArtist,
    linkSpotify,
    create,
    update,
    triggerEnrich,
    updateEnrichment,
    nearestNeighbours,
    merge,
    unmerge,
  };
};
